import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import type {
	NumericTextBoxChangeEvent,
	NumericTextBoxFocusEvent,
} from "@tql/ui-components-react/kendo/inputs";
import { NumericTextBox } from "@tql/ui-components-react/tql/inputs/beta";
import { trash_01 } from "@tql/ui-components-react/untitled/icons";
import { memo, useCallback, useState } from "react";

import type { Invoice } from "~/shared/schemas/invoice-schema.ts";
import { formatCurrency } from "~/shared/utils/string-fns.ts";

import { PAYMENT_CONSTRAINTS } from "../../../../utils/constants.ts";
import styles from "./invoice-payment-card-mobile.module.css";

type InvoicePaymentCardMobileProps = Readonly<{
	invoice: Invoice;
	onAmountChange: (invoiceId: string, amount: number) => void;
	onDeleteInvoice: (invoiceId: string) => void;
}>;

export const InvoicePaymentCardMobile = memo<InvoicePaymentCardMobileProps>(
	({
		invoice,
		onAmountChange,
		onDeleteInvoice,
	}: InvoicePaymentCardMobileProps) => {
		const invoiceId = String(invoice.invoiceID);
		const [localValue, setLocalValue] = useState<number | null>(
			invoice.balanceAmt ?? 0,
		);

		const handleChange = useCallback(
			(event: NumericTextBoxChangeEvent) => {
				const value = event.target.value;
				const positiveValue = value !== null && value < 0 ? 0 : value;
				setLocalValue(positiveValue);
				onAmountChange(invoiceId, positiveValue ?? 0);
			},
			[invoiceId, onAmountChange],
		);

		const handleFocus = useCallback((event: NumericTextBoxFocusEvent) => {
			// Select all text on focus so next keystroke overwrites
			setTimeout(() => {
				event.target.element?.select();
			}, 0);
		}, []);

		const handleBlur = useCallback(() => {
			if (localValue === null || localValue <= 0) {
				setLocalValue(0);
				onAmountChange(invoiceId, 0);
			}
		}, [localValue, invoiceId, onAmountChange]);

		const handleDelete = useCallback(() => {
			onDeleteInvoice(invoiceId);
		}, [invoiceId, onDeleteInvoice]);

		return (
			<div
				className={styles.invoicePaymentCard}
				data-testid={"invoice-payment-card"}
			>
				<div className={styles.invoicePaymentCardDetails}>
					<div className={styles.invoicePaymentCardField}>
						<span className={styles.fieldLabel}>Customer PO #</span>
						<span className={styles.fieldValue}>{invoice.custPO}</span>
					</div>
					<div className={styles.invoicePaymentCardField}>
						<span className={styles.fieldLabel}>TQL PO #</span>
						<span className={styles.fieldValue}>{invoice.poNumber}</span>
					</div>
					<div className={styles.invoicePaymentCardField}>
						<span className={styles.fieldLabel}>Balance</span>
						<span className={styles.fieldValue}>
							{formatCurrency(invoice.balanceAmt)}
						</span>
					</div>
				</div>
				<div className={styles.invoicePaymentCardAmountRow}>
					<span
						className={styles.fieldLabel}
						id={`invoice-payment-amount-label-${invoiceId}`}
					>
						Pay Selected (USD)
						<span className={"required"}> *</span>
					</span>
					<div className={styles.invoicePaymentCardAmountControls}>
						<div className={styles.amountInputWrapper}>
							<span className={styles.currencyPrefix}>$</span>
							<NumericTextBox
								aria-labelledby={`invoice-payment-amount-label-${invoiceId}`}
								format={"n2"}
								id={`invoice-payment-amount-${invoiceId}`}
								max={PAYMENT_CONSTRAINTS.MAX_AMOUNT}
								min={0}
								onBlur={handleBlur}
								onChange={handleChange}
								onFocus={handleFocus}
								spinners={false}
								value={localValue}
							/>
						</div>
						<button
							aria-label={"Remove invoice"}
							className={styles.iconButton}
							data-testid={"invoice-payment-card-delete"}
							onClick={handleDelete}
							type={"button"}
						>
							<SvgIcon
								icon={trash_01}
								size={"xlarge"}
								style={{ color: "var(--kendo-color-error-on-surface)" }}
							/>
						</button>
					</div>
				</div>
			</div>
		);
	},
);

InvoicePaymentCardMobile.displayName = "InvoicePaymentCardMobile";
